/**
 * Agent Bell Notifications — singleton service.
 *
 * Mirrors the waPendingCount.js pattern: one poll drives every subscriber
 * (bell badge, dropdown list, toast popup) instead of each component polling
 * independently. Polls every 10s (only while the tab is visible) for lead-
 * assignment notifications — see manijment-api services/ai-service/
 * agentNotifications.js for the backend write side.
 *
 * API:
 *   agentNotificationsService.subscribe(setter) → unsubscribe fn.
 *     setter is called immediately with { notifications, unreadCount } and
 *     again on every poll.
 *   agentNotificationsService.subscribeNew(onNew) → unsubscribe fn.
 *     onNew(notification) fires once per notification the FIRST time it's
 *     seen (not on every poll) — drives the toast popup + sound.
 *   agentNotificationsService.markRead(ids)   → mark specific ids read.
 *   agentNotificationsService.markAllRead()   → mark everything read.
 *   agentNotificationsService.refresh()       → force an immediate fetch.
 */

import axios from 'axios';

const AI_PATH    = process.env.REACT_APP_AI_PATH;
const TIMEOUT_MS = 10000;

// 2026-10-07 (Shailendra): lead-assignment notifications only have anything
// NEW to report right after the assignment cron runs, at :05 and :35 of every
// hour (services/ai-service/cron.js) — a flat 10s poll all day was checking
// for nothing, nearly always. Poll tightly (30s) only within a few minutes of
// either fire time; otherwise schedule exactly for the next window instead of
// a blind fixed delay, so it can't drift past one.
const POLL_MS_ACTIVE      = 30 * 1000;
const ASSIGNMENT_MINUTES  = [5, 35];
const WINDOW_SLACK_MIN    = 2; // :03–:07 and :33–:37 count as "active"

function isInAssignmentWindow(d = new Date()) {
  const m = d.getMinutes();
  return ASSIGNMENT_MINUTES.some((am) => Math.abs(m - am) <= WINDOW_SLACK_MIN);
}

// Exact ms until the next window OPENS (:03 or :33), not a flat 30-min guess —
// avoids drifting past a window on an unlucky phase.
function msUntilNextWindow(d = new Date()) {
  const windowStarts = ASSIGNMENT_MINUTES.map((am) => am - WINDOW_SLACK_MIN);
  const nowFrac = d.getMinutes() + d.getSeconds() / 60 + d.getMilliseconds() / 60000;
  const next = windowStarts.find((st) => st > nowFrac);
  const minutesAway = next !== undefined ? next - nowFrac : (60 - nowFrac) + windowStarts[0];
  return Math.max(0, Math.round(minutesAway * 60000));
}

let current     = { notifications: [], unreadCount: 0 };
const subscribers    = new Set();
const newSubscribers = new Set();
let timer       = null;
let inFlight    = null;
let knownIds    = null; // null = not yet initialized (first fetch seeds it, no toasts)

const getAdminId = () => {
  try { return localStorage.getItem('adminId') || null; } catch (e) { return null; }
};

// ─── Best-effort notification sound (Web Audio API — no asset file needed) ──
// Autoplay policies block sound before any user gesture on the page; we lazily
// create/resume the AudioContext on the first click anywhere so it's ready by
// the time a real notification arrives. If audio hardware/permission isn't
// available this just silently no-ops — never throws up to the caller.
let audioCtx = null;
function primeAudio() {
  try {
    if (!audioCtx) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      audioCtx = new Ctx();
    }
    if (audioCtx.state === 'suspended') audioCtx.resume().catch(() => {});
  } catch (e) { /* no audio hardware / blocked — ignore */ }
}
if (typeof document !== 'undefined') {
  ['click', 'keydown', 'touchstart'].forEach((evt) =>
    document.addEventListener(evt, primeAudio, { once: true, passive: true })
  );
}

function playChime() {
  try {
    if (!audioCtx) return; // not primed yet — skip rather than throw
    const osc  = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, audioCtx.currentTime);
    osc.frequency.setValueAtTime(660, audioCtx.currentTime + 0.12);
    gain.gain.setValueAtTime(0.0001, audioCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.2, audioCtx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, audioCtx.currentTime + 0.35);
    osc.connect(gain).connect(audioCtx.destination);
    osc.start();
    osc.stop(audioCtx.currentTime + 0.4);
  } catch (e) { /* ignore — sound is best-effort only */ }
}

async function fetchNow() {
  if (inFlight) return inFlight;
  const agentId = getAdminId();
  if (!agentId || !AI_PATH) return;
  inFlight = axios.get(`${AI_PATH}/api/ai/whatsapp/notifications`, {
    params: { agentId, limit: 20 },
    timeout: TIMEOUT_MS,
  })
    .then((res) => {
      const notifications = res.data?.notifications || [];
      const unreadCount   = Number(res.data?.unreadCount ?? 0);

      if (knownIds === null) {
        // First fetch after page load — seed the seen-set silently, no toasts
        // for notifications that already existed before this session opened.
        knownIds = new Set(notifications.map((n) => n._id));
      } else {
        const fresh = notifications.filter((n) => !knownIds.has(n._id));
        if (fresh.length > 0) {
          fresh.forEach((n) => knownIds.add(n._id));
          // Oldest-first so a toast queue reads top-to-bottom in arrival order.
          fresh.reverse().forEach((n) => {
            playChime();
            for (const cb of newSubscribers) cb(n);
          });
        }
      }

      current = { notifications, unreadCount };
      for (const set of subscribers) set(current);
    })
    .catch((err) => {
      console.warn('[AgentNotifications] fetch failed:', err.message);
    })
    .finally(() => { inFlight = null; });
  return inFlight;
}

function scheduleNext() {
  const delay = isInAssignmentWindow() ? POLL_MS_ACTIVE : msUntilNextWindow();
  timer = setTimeout(async () => {
    if (typeof document === 'undefined' || document.visibilityState === 'visible') {
      await fetchNow();
    }
    scheduleNext();
  }, delay);
}

function startPoll() {
  if (timer) return;
  scheduleNext();
}

function stopPoll() {
  if (!timer) return;
  clearTimeout(timer);
  timer = null;
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') fetchNow();
  });
}

async function markRead(ids) {
  const agentId = getAdminId();
  if (!agentId || !AI_PATH) return;
  // Optimistic local update so the badge/list feel instant.
  current = {
    unreadCount: Math.max(0, current.unreadCount - ids.filter((id) =>
      current.notifications.some((n) => n._id === id && !n.read)
    ).length),
    notifications: current.notifications.map((n) => (ids.includes(n._id) ? { ...n, read: true } : n)),
  };
  for (const set of subscribers) set(current);
  try {
    await axios.post(`${AI_PATH}/api/ai/whatsapp/notifications/mark-read`, { agentId, ids });
  } catch (err) {
    console.warn('[AgentNotifications] markRead failed:', err.message);
  }
}

async function markAllRead() {
  const agentId = getAdminId();
  if (!agentId || !AI_PATH) return;
  current = {
    unreadCount: 0,
    notifications: current.notifications.map((n) => ({ ...n, read: true })),
  };
  for (const set of subscribers) set(current);
  try {
    await axios.post(`${AI_PATH}/api/ai/whatsapp/notifications/mark-read`, { agentId });
  } catch (err) {
    console.warn('[AgentNotifications] markAllRead failed:', err.message);
  }
}

const agentNotificationsService = {
  subscribe(setter) {
    subscribers.add(setter);
    setter(current);
    if (subscribers.size === 1) {
      startPoll();
      fetchNow();
    }
    return () => {
      subscribers.delete(setter);
      if (subscribers.size === 0 && newSubscribers.size === 0) stopPoll();
    };
  },
  subscribeNew(onNew) {
    newSubscribers.add(onNew);
    return () => newSubscribers.delete(onNew);
  },
  markRead,
  markAllRead,
  refresh: fetchNow,
  get current() { return current; },
};

export default agentNotificationsService;
